import { Helmet } from "react-helmet";
import Breadcrumb from "../components/Breadcrumb";
import classes from "./ReturnRefundPolicy.module.css";
import { Link } from "react-router-dom";

export default function ReturnRefundPolicy() {
  return (
    <>
      <Helmet>
        <title>
          Return & Refund Policy - How TechMatter UK Protects Your Personal Data
        </title>
        <meta
          name="description"
          content="At TechMatter UK, your privacy matters. Learn how we securely collect, use, and protect customer information while shopping for IT hardware online."
        />
        <link
          rel="canonical"
          href="https://techmatter.co.uk/return-refund-policy"
        />
      </Helmet>

      <Breadcrumb currentPage="Return & Refund Policy" />
      <div className={`container ${classes.returnRefundContainer}`}>
        <p className={classes.paragraph}>
          We offer a 30-day return policy, which means you have 30 days after
          receiving your item to request a return.
        </p>
        <p className={classes.paragraph}>
          To return an item, it must be in its original packaging, unused, with
          tags, and include the receipt or proof of purchase.
        </p>
        <p className={classes.paragraph}>
          To initiate a return, please reach out to us at{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>
          . Once we have accepted your return, we will provide you with a
          shipping label and instructions on how and where to send your package.
          Please note that we do not accept returns that have not been requested
          beforehand.
        </p>
        <p className={classes.paragraph}>
          If you have any questions about returns, please don’t hesitate to
          contact at{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>
          .
        </p>
        <h2 className={classes.subheading}>Damages and issues</h2>
        <p className={classes.paragraph}>
          Please inspect your order upon reception and contact immediately if
          the item is defective, damaged, or if you receive the wrong item so
          that we can evaluate the issue and make it right.
        </p>
        <h2 className={classes.subheading}>
          Exceptions / non-returnable items
        </h2>
        <p className={classes.paragraph}>
          Unfortunately, we cannot accept returns on sale items or gift cards.
        </p>
        <h2 className={classes.subheading}>Exchanges</h2>
        <p className={classes.paragraph}>
          The fastest way to ensure you get what you want is to return the item
          you have, and once the return is accepted, make a separate purchase
          for the new item.
        </p>
        <h2 className={classes.subheading}>Refunds</h2>
        <p className={classes.paragraph}>
          We will notify you once we’ve received and inspected your return, and
          let you know if the refund was approved or not. If approved, you’ll be
          automatically refunded on your original payment method. Please
          remember it can take some time for your bank or credit card company to
          process and post the refund too.
        </p>
        <h2 className={classes.subheading}>Our promise</h2>
        <p className={classes.paragraph}>
          We work hard every day to enrich the lives of our customers through
          technology, whether you come to us online, visit our stores, or invite
          us to your home. If you are not completely satisfied with your
          purchase, let us help you with a replacement, return, or repair. This
          policy applies to purchases made in the TechMatter store.
        </p>
        <h2 className={classes.subheading}>Ineligible items</h2>
        <p className={classes.paragraph}>
          The following categories are not eligible for a “Change of Mind”
          statement:
        </p>
        <ul className={classes.list}>
          <li>Personalized items</li>
          <li>Gift cards</li>
          <li>Orders for commercial or non-domestic use</li>
        </ul>
        <h2 className={classes.subheading}>
          Processing your “Change of mind” return
        </h2>
        <p className={classes.paragraph}>
          For all “Change of Mind” returns, please email{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>{" "}
          to our Customer Service Team. A member of our customer service team
          will help you book your return.
        </p>
        <p className={classes.paragraph}>
          We require that photographs be sent with your return request, showing
          that the item and its packaging are both in acceptable condition,
          before a “Change of Mind” return can be processed.
        </p>
        <p className={classes.paragraph}>
          Once we have received the returned item from you and it is found to be
          in acceptable condition, we will provide you with a refund or store
          credit (whichever you prefer).
        </p>
        <h2 className={classes.subheading}>Deduction</h2>
        <p className={classes.paragraph}>
          The refund or store credit amount issued will not include any shipping
          charges you may have paid in the original order.
        </p>
        <p className={classes.paragraph}>
          We will deduct any freight charges associated with returning the item
          from the final refund or store credit amount granted to you.
        </p>
        <h2 className={classes.subheading}>
          Lost or stolen packages (if applicable)
        </h2>
        <p className={classes.paragraph}>
          TechMatter is not responsible for lost or stolen packages whose
          delivery is confirmed to the address entered for an order. Upon
          request, TechMatter will confirm delivery to the address provided,
          delivery date, tracking information, and carrier information for the
          customer to investigate.
        </p>
        <h2 className={classes.subheading}>Cancellation after order</h2>
        <p className={classes.paragraph}>
          You have the right to cancel your order within 24 hours without any
          reason. To cancel your order, please contact our customer success
          team, email us at{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>{" "}
          and provide us with your name and order number. We will refund the
          purchase amount to the original payment method within 5 business days.
        </p>
        <h2 className={classes.subheading}>How to return an item</h2>
        <p className={classes.paragraph}>
          Gather the purchases you wish to return, along with the original
          content and packaging
        </p>
        <p className={classes.paragraph}>
          You can return any item purchased from the TechMatter store. For
          faster processing of returns, please bring your packing slip (if you
          received one), receipt, the credit card used to make your purchase,
          and a valid photo ID.
        </p>
        <h2 className={classes.subheading}>Like new condition</h2>
        <p className={classes.paragraph}>
          Items must be returned in like-new condition. Items damaged,
          unsanitary, dented, scratched, or missing major content may be refused
          a return. Clothing must not be worn or washed, and its original tags
          must be attached for us to accept a return.
        </p>
        <h2 className={classes.subheading}>Damage on arrival:</h2>
        <p className={classes.paragraph}>
          If your product arrived damaged, please take photos and videos and
          send them to our customer service at{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>{" "}
          . After checking the support and confirming the case, you will have
          two options: we will send you a new product or give you a 100% refund.
        </p>
        <h2 className={classes.subheading}>
          Defective or broken manufacture after use:
        </h2>
        <p className={classes.paragraph}>
          You have 30 days after the arrival of your product to report any
          problems with your product. If the product does not work during this
          period or is defective, you can return it to us at your expense and we
          will refund the full amount by store credit so that you can order your
          new product.
        </p>
        <h2 className={classes.subheading}>Refund Requirements:</h2>
        <p className={classes.paragraph}>
          Refunds will be refunded to your original payment method. It may take
          3 to 5 business days for your refund to be issued and your account to
          be accredited.
        </p>
        <p className={classes.paragraph}>
          Promotions and discounts will not be refunded.
        </p>
        <p className={classes.paragraph}>
          If you have questions please contact:
        </p>
        <h2 className={classes.subheading}>CLICK HERE TO CONTACT DIRECTLY!</h2>
        <p className={classes.paragraph}>For more information</p>
        <p className={classes.paragraph}>Contact</p>
        <p className={classes.paragraph}>
          <strong>Working Hours: </strong>Mon – Fri / 8:00AM – 5:00PM (GMT).
        </p>
        <p className={classes.paragraph}>
          <strong>Phone: </strong>{" "}
          <Link to="tel:+442031293546">+442031293546</Link>
        </p>
        <p className={classes.paragraph}>
          <strong>Email: </strong>{" "}
          <Link to="mailto:sales@techmatter.co.uk">sales@techmatter.co.uk</Link>
        </p>
      </div>
    </>
  );
}
